/* Gift-with-purchase reconciler, multi-slot.
   Liquid renders the desired state per gift (want 0/1, current qty) fresh on
   every cart render; this component applies everything that differs in ONE
   /cart/update.js call — adds, removals, and quantity clamps together — so
   the mutate→re-render loop settles in a single step. Both gift products are
   $0.00 in admin: money is safe server-side no matter what this code does. */

if (!customElements.get('mov-cart-gifts')) {
  customElements.define(
    'mov-cart-gifts',
    class MovCartGifts extends HTMLElement {
      connectedCallback() {
        this.dataset.movInit = '1';
        // Wait a tick so the surrounding drawer/page finishes rendering.
        // setTimeout over requestAnimationFrame: rAF can be skipped for
        // occluded/animating containers; a macrotask always runs.
        setTimeout(() => this.evaluate(), 0);
        this.watchManualRemoval();
      }

      get slots() {
        try {
          return JSON.parse(this.dataset.slots || '[]');
        } catch {
          return [];
        }
      }

      dismissKey(variantId) {
        return `mov-gift-dismissed-${variantId}`;
      }

      isDismissed(variantId) {
        try {
          return sessionStorage.getItem(this.dismissKey(variantId)) === 'true';
        } catch {
          return false;
        }
      }

      setDismissed(variantId, value) {
        try {
          if (value) sessionStorage.setItem(this.dismissKey(variantId), 'true');
          else sessionStorage.removeItem(this.dismissKey(variantId));
        } catch {
          /* storage unavailable — auto-add may repeat, which is harmless */
        }
      }

      evaluate() {
        const updates = {};
        this.slots.forEach((s) => {
          if (!s.v) return;
          if (s.w === 1) {
            if (s.q === 0 && s.a && !this.isDismissed(s.v)) updates[s.v] = 1;
            else if (s.q > 1) updates[s.v] = 1; // clamp tampered quantity
          } else {
            if (s.q > 0) updates[s.v] = 0;
            // Re-arm auto-add once the shopper drops below the threshold.
            this.setDismissed(s.v, false);
          }
        });
        if (Object.keys(updates).length === 0) return;
        this.mutateCart(updates);
      }

      /* If the shopper deletes a gift line themselves, respect that choice
         for the rest of the session (until they drop below and re-cross). */
      watchManualRemoval() {
        const scope = this.closest('cart-drawer') || document;
        const variants = new Set(this.slots.map((s) => String(s.v)));
        scope.querySelectorAll('cart-remove-button button[data-variant-id]').forEach((btn) => {
          if (variants.has(btn.dataset.variantId)) {
            btn.addEventListener('click', () => this.setDismissed(btn.dataset.variantId, true), { once: true });
          }
        });
      }

      sectionsToRender() {
        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const cartItems = document.getElementById('main-cart-items');
        if (cartItems && cartItems.dataset.id) sections.push(cartItems.dataset.id);
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter && cartFooter.dataset.id) sections.push(cartFooter.dataset.id);
        return sections;
      }

      mutateCart(updates) {
        fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: this.sectionsToRender() }),
        })
          .then((response) => response.json())
          .then((state) => {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof state.item_count === 'number') {
              drawer.classList.toggle('is-empty', state.item_count === 0);
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mov-cart-gifts', cartData: state });
            }
            if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
              // renderContents re-opens the drawer, so only use it when the
              // drawer is already active.
              if (drawer.classList.contains('active')) {
                drawer.renderContents(state);
                return;
              }
            }
            this.replaceSections(state.sections);
          })
          .catch(() => {
            /* Network hiccup: the next cart interaction re-evaluates anyway. */
          });
      }

      replaceSections(sections) {
        if (!sections) return;
        const targets = [
          { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
        const cartItems = document.getElementById('main-cart-items');
        if (cartItems && cartItems.dataset.id) {
          targets.push({ id: 'main-cart-items', section: cartItems.dataset.id, selector: '.js-contents' });
        }
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter && cartFooter.dataset.id) {
          targets.push({ id: 'main-cart-footer', section: cartFooter.dataset.id, selector: '.js-contents' });
        }
        targets.forEach(({ id, section, selector }) => {
          const target = document.getElementById(id);
          const html = sections[section];
          if (!target || !html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const replacement = doc.querySelector(selector);
          if (!replacement) return;
          const inner = target.querySelector(selector);
          if (inner && selector === '.js-contents') inner.innerHTML = replacement.innerHTML;
          else target.innerHTML = replacement.innerHTML;
        });
      }
    }
  );
}
